/**
 * 分类分析工具
 * 用于分析文章内容，识别标题和【】中的元素
 */

/**
 * 分析文章内容，提取分类和元素
 * @param {string} content - HTML格式的文章内容
 * @returns {Object} 分析结果
 */
export function analyzeCategoryAndElements(content) {
  try {
    // 创建DOM解析器
    const parser = new DOMParser()
    const doc = parser.parseFromString(content, 'text/html')
    
    // 获取所有文本内容，保留段落结构但合并被分割的元素
    let textContent = ''
    
    // 遍历所有文本节点和元素，保留结构
    function extractTextWithStructure(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim()
        if (text) {
          // 检查是否是元素的一部分（包含【或】）
          if (text.includes('【') || text.includes('】')) {
            // 不添加换行，让元素内容保持在同一行
            textContent += text
          } else {
            textContent += text + '\n'
          }
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // 对于块级元素，在前后添加换行
        const isBlockElement = ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BR'].includes(node.tagName)
        
        if (isBlockElement && textContent && !textContent.endsWith('\n')) {
          textContent += '\n'
        }
        
        // 递归处理子节点
        for (let child of node.childNodes) {
          extractTextWithStructure(child)
        }
        
        if (isBlockElement) {
          textContent += '\n'
        }
      }
    }
    
    extractTextWithStructure(doc.body)
    
    // 后处理：合并被分割的元素
    textContent = textContent.replace(/【([^】]*)\n([^】]*?)】/g, '【$1$2】')
    
    // 分析结果
    const result = {
      categories: [],
      totalElements: 0,
      isValid: false
    }
    
    // 按行分割内容，过滤空行，并清理多余的空白字符
    const lines = textContent.split('\n')
      .map(line => line.trim())
      .filter(line => line)
      .filter(line => line.length > 0)
    
    console.log('=== 开始分析文档 ===')
    console.log('总行数:', lines.length)
    console.log('前10行内容:', lines.slice(0, 10))
    
    let currentCategory = null
    let categoryIndex = 0
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      
      // 检查是否是标题行
      // 1. # 或 ## 开头，或数字编号开头，且不包含【】
      // 2. 单独一行的短文本（可能是二级标题），且下一行包含元素
      const hasHashOrNumber = (line.match(/^#+\s/) || /^\d+\.\s/.test(line)) && !line.includes('【')
      const hasElements = line.includes('【') && line.includes('】')
      
      // 检查是否是潜在的二级标题（短文本，下面有元素）
      let isPotentialSubtitle = false
      if (!hasHashOrNumber && !hasElements && line.length <= 20) {
        // 检查后面几行是否有元素
        for (let j = i + 1; j < Math.min(i + 10, lines.length); j++) {
          const nextLine = lines[j].trim()
          if (nextLine.includes('【') && nextLine.includes('】')) {
            isPotentialSubtitle = true
            break
          }
          // 如果遇到另一个可能的标题，停止查找
          if ((nextLine.match(/^#+\s/) || /^\d+\.\s/.test(nextLine)) && !nextLine.includes('【')) {
            break
          }
          // 如果遇到另一个短文本且距离较远，可能是另一个标题
          if (nextLine.length <= 20 && !nextLine.includes('【') && j - i > 5) {
            break
          }
        }
      }
      
      const isTitle = hasHashOrNumber || isPotentialSubtitle
      
      console.log(`第${i+1}行: "${line.substring(0, 50)}..." - 是标题: ${isTitle}, 有元素: ${hasElements}, 潜在子标题: ${isPotentialSubtitle}`)
      
      if (isTitle) {
        // 提取标题文本
        let titleText = line
        if (line.match(/^#+\s/)) {
          titleText = line.replace(/^#+\s*/, '').replace(/^\d+\.\s*/, '').trim()
        } else if (/^\d+\.\s/.test(line)) {
          titleText = line.replace(/^\d+\.\s*/, '').trim()
        } else {
          // 对于二级标题，直接使用原文本
          titleText = line.trim()
        }
        
        // 保存上一个分类（先处理其内容）
        if (currentCategory) {
          if (currentCategory.contentLines && currentCategory.contentLines.length > 0) {
            // 将所有内容行合并为一个字符串
            const categoryContent = currentCategory.contentLines.join(' ').trim()
            console.log(`分类 "${currentCategory.title}" 的完整内容:`, categoryContent.substring(0, 200) + '...')
            
            // 从合并的内容中提取所有元素
            const elements = extractElementsFromText(categoryContent)
            currentCategory.elements = elements
            console.log(`为分类 "${currentCategory.title}" 提取到 ${elements.length} 个元素`)
          }
          
          if (currentCategory.elements.length > 0) {
            result.categories.push(currentCategory)
            console.log(`保存分类: ${currentCategory.title}，包含 ${currentCategory.elements.length} 个元素`)
          }
        }
        
        // 创建新分类
        currentCategory = {
          id: `category_${categoryIndex++}`,
          title: titleText,
          elements: [],
          contentLines: [], // 收集包含元素的行
          color: getCategoryColor(categoryIndex - 1)
        }
        console.log(`创建新分类 ${categoryIndex}: ${titleText}`)
        
      } else if (hasElements) {
        // 包含【】的行，先收集到当前分类中
        if (!currentCategory) {
          // 创建一个默认分类
          currentCategory = {
            id: `category_${categoryIndex++}`,
            title: '未分类',
            elements: [],
            contentLines: [], // 收集包含元素的行
            color: getCategoryColor(categoryIndex - 1)
          }
          console.log('创建默认分类: 未分类')
        }
        
        // 将包含元素的行添加到内容中
        if (!currentCategory.contentLines) {
          currentCategory.contentLines = []
        }
        currentCategory.contentLines.push(line)
        console.log(`为分类 "${currentCategory.title}" 添加内容行`)
      }
    }
    
    // 处理最后一个分类的内容
    if (currentCategory) {
      if (currentCategory.contentLines && currentCategory.contentLines.length > 0) {
        // 将所有内容行合并为一个字符串
        const categoryContent = currentCategory.contentLines.join(' ').trim()
        console.log(`分类 "${currentCategory.title}" 的完整内容:`, categoryContent.substring(0, 200) + '...')
        
        // 从合并的内容中提取所有元素
        const elements = extractElementsFromText(categoryContent)
        currentCategory.elements = elements
        console.log(`为分类 "${currentCategory.title}" 提取到 ${elements.length} 个元素`)
      }
      
      if (currentCategory.elements.length > 0) {
        result.categories.push(currentCategory)
        console.log(`添加最后一个分类: ${currentCategory.title}，包含 ${currentCategory.elements.length} 个元素`)
      }
    }
    
    // 计算总元素数量
    result.totalElements = result.categories.reduce((total, category) => {
      return total + category.elements.length
    }, 0)
    
    // 检查是否符合要求：至少有一个分类，且每个分类都有元素
    result.isValid = result.categories.length > 0 && 
                     result.categories.every(cat => cat.elements.length > 0) &&
                     result.totalElements > 0
    
    console.log('=== 分类分析结果 ===')
    console.log(`总共识别到 ${result.categories.length} 个分类，${result.totalElements} 个元素`)
    result.categories.forEach((category, index) => {
      console.log(`分类 ${index + 1}: ${category.title} (${category.elements.length} 个元素)`)
    })
    console.log('==================')
    
    return result
    
  } catch (error) {
    console.error('分类分析失败:', error)
    return {
      categories: [],
      totalElements: 0,
      isValid: false,
      error: error.message
    }
  }
}

/**
 * 从文本中提取【】中的元素（处理跨行内容）
 * @param {string} text - 文本内容
 * @returns {Array} 元素数组
 */
function extractElementsFromText(text) {
  const elements = []
  
  console.log('开始提取元素，原始文本:', text.substring(0, 300) + '...')
  
  // 先处理格式问题：将【元素名：描述】格式转换为【元素名】描述
  let processedText = text.replace(/【([^】]*?)[:：]([^】]*?)】/g, '【$1】$2')
  console.log('格式处理后的文本:', processedText.substring(0, 300) + '...')
  
  // 使用正则表达式匹配【】中的内容及其后面直到下一个【】的所有内容
  const regex = /【([^】]+)】([^【]*?)(?=【|$)/g
  let match
  let matchIndex = 0
  
  while ((match = regex.exec(processedText)) !== null) {
    matchIndex++
    const elementText = match[1].trim()
    if (elementText) {
      // 提取元素后面的完整描述内容（包括跨行的内容）
      let description = ''
      if (match[2]) {
        description = match[2].trim()
        // 清理描述内容：移除开头的符号，保留完整的描述文本
        description = description.replace(/^[-—:：]\s*/, '').trim()
        // 清理多余的空白字符，但保持文本的连续性
        description = description.replace(/\s+/g, ' ').trim()
      }
      
      console.log(`匹配 ${matchIndex}: 元素="${elementText}", 描述="${description.substring(0, 100)}${description.length > 100 ? '...' : ''}"`)
      
      elements.push({
        id: `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        text: elementText,
        description: description,
        originalText: text
      })
    }
  }
  
  console.log(`总共提取到 ${elements.length} 个元素`)
  return elements
}

/**
 * 从一行文本中提取【】中的元素
 * @param {string} line - 文本行
 * @returns {Array} 元素数组
 */
function extractElementsFromLine(line) {
  const elements = []
  
  // 使用正则表达式匹配【】中的内容及其后面的完整描述
  const regex = /【([^】]+)】([^【]*?)(?=【|$)/g
  let match
  
  while ((match = regex.exec(line)) !== null) {
    const elementText = match[1].trim()
    if (elementText) {
      // 提取元素后面的完整描述内容
      let description = ''
      if (match[2]) {
        description = match[2].trim()
        // 清理描述内容：移除开头的符号，保留完整的描述文本
        description = description.replace(/^[-—]\s*/, '').trim()
      }
      
      elements.push({
        id: `element_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        text: elementText,
        description: description,
        originalLine: line
      })
    }
  }
  
  return elements
}

/**
 * 获取分类对应的颜色
 * @param {number} index - 分类索引
 * @returns {string} 颜色值
 */
function getCategoryColor(index) {
  // 使用指定的5种颜色
  const colors = [
    '#CB7773', // 暖红色
    '#776FD2', // 紫色
    '#70A8B6', // 青蓝色
    '#CF9C76', // 暖橙色
    '#54668D'  // 深蓝色
  ]
  
  return colors[index % colors.length]
}

/**
 * 验证内容是否符合分析要求
 * @param {string} content - 文章内容
 * @returns {Object} 验证结果
 */
export function validateContent(content) {
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(content, 'text/html')
    const textContent = doc.body.textContent || doc.body.innerText || ''
    
    // 检查是否包含【】元素
    const hasElements = /【[^】]+】/.test(textContent)
    
    if (!hasElements) {
      return {
        isValid: false,
        message: '文章内容中没有找到【】格式的元素'
      }
    }
    
    // 分析内容
    const analysisResult = analyzeCategoryAndElements(content)
    
    if (!analysisResult.isValid) {
      return {
        isValid: false,
        message: '文章格式不符合要求。请确保：\n1. 有标题行（可以带编号，如"1. 标题"）\n2. 标题下有包含【元素名】的内容\n3. 【】中包含具体的元素内容'
      }
    }
    
    return {
      isValid: true,
      message: `识别到 ${analysisResult.categories.length} 个分类，共 ${analysisResult.totalElements} 个元素`,
      data: analysisResult
    }
    
  } catch (error) {
    return {
      isValid: false,
      message: '内容验证失败：' + error.message
    }
  }
}

/**
 * 将分析结果转换为白板数据格式
 * @param {Object} analysisResult - 分析结果
 * @returns {Object} 白板数据
 */
export function convertToDraftData(analysisResult) {
  try {
    const draftData = {
      type: 'category-analysis',
      title: '分类元素分析',
      categories: analysisResult.categories,
      metadata: {
        totalCategories: analysisResult.categories.length,
        totalElements: analysisResult.totalElements,
        createdAt: new Date().toISOString()
      }
    }
    
    return draftData
  } catch (error) {
    console.error('转换白板数据失败:', error)
    return null
  }
}
